import React, { useCallback } from 'react';
import { rem } from 'design-system';
import Amount from 'design-system/Amount';
import FlatListItemCard from 'design-system/FlatListItemCard';
import ListEmptyView from 'design-system/ListEmptyView';
import { SettingsItem, SettingsItemContent, SettingsItemRightContent, SettingsItemTitle, } from 'design-system/SettingsItem';
import Text from 'design-system/Text';
import useStyles from 'hooks/useStyles';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import createStyleSheets from 'utils/createStyleSheets';
import { ActivityIndicator, View } from 'react-native';
import ConnectSuggestedBankLabel from './ConnectSuggestedBankLabel';
import CreditReportLenderLogo from './CreditReportLenderLogo';
import { CreditReportOfferCard } from './CreditReportOfferCards';
import SwitchSuggestionLabel from './SwitchSuggestionLabel';
const sectionTitles = {
    bankAccount: 'Bank account overdrafts',
    creditCard: 'Credit cards',
    mortgage: 'Mortgages',
    personalLoan: 'Personal loans',
    hirePurchase: 'Hire purchases',
    studentLoan: 'Student loans',
    rental: 'Rentals',
    utility: 'Utilities',
    other: 'Other accounts',
};
export const ACCOUNT_SECTION_ORDER = [
    'bankAccount',
    'creditCard',
    'personalLoan',
    'hirePurchase',
    'mortgage',
    'studentLoan',
    'rental',
    'utility',
    'other',
];
const accountFallbackTitle = 'Account';
const getAccountTitle = (account) => account.lenderName ?? account.accountTypeName ?? accountFallbackTitle;
export const buildAccountListEntries = (sections) => {
    const entries = [];
    sections.forEach(({ key, accounts, cards }) => {
        if (accounts.length === 0 && !cards?.length)
            return;
        // A section with no accounts hands its title to the first card, which
        // renders it inside its own Collapsible so header and card collapse
        // together on dismiss rather than leaving an empty section behind.
        const isCardOnlySection = accounts.length === 0;
        if (!isCardOnlySection) {
            entries.push({
                type: 'sectionHeader',
                id: `header-${key}`,
                title: sectionTitles[key],
            });
        }
        accounts.forEach((account, index) => {
            entries.push({
                type: 'account',
                id: `${key}-${index}`,
                sectionKey: key,
                account,
                index,
                itemsLength: accounts.length,
            });
        });
        cards?.forEach((cardKey, index) => {
            entries.push({
                type: 'card',
                id: `${key}-card-${cardKey}`,
                sectionKey: key,
                cardKey,
                sectionTitle: isCardOnlySection && index === 0 ? sectionTitles[key] : undefined,
            });
        });
    });
    return entries;
};
export const accountListKeyExtractor = (entry) => entry.id;
const CreditReportAccountListRow = React.memo(({ entry }) => {
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const onPress = useCallback(() => {
        if (entry.type !== 'account')
            return;
        navigation.navigate('CreditReportAccountDetailScreen', {
            accountId: entry.account.accountId ?? undefined,
        });
    }, [entry, navigation]);
    if (entry.type === 'sectionHeader') {
        return (<View style={styles.sectionHeader}>
          <Text Gray Text-16>
            {entry.title}
          </Text>
        </View>);
    }
    if (entry.type === 'card') {
        return (<CreditReportOfferCard cardKey={entry.cardKey} sectionTitle={entry.sectionTitle}/>);
    }
    const { account, sectionKey } = entry;
    const { balance } = account;
    const showBalance = balance !== null && Number.isFinite(balance) && sectionKey !== 'rental';
    return (<FlatListItemCard index={entry.index} itemsLength={entry.itemsLength} style={styles.card}>
        <SettingsItem renderIcon={() => (<CreditReportLenderLogo inSettingsItem iconUrl={account.iconUrl} name={account.lenderName}/>)} onPress={onPress}>
          <SettingsItemContent>
            <SettingsItemTitle>{getAccountTitle(account)}</SettingsItemTitle>
            <ConnectSuggestedBankLabel tuAccountId={account.accountId}/>
            <SwitchSuggestionLabel account={account}/>
          </SettingsItemContent>
          {showBalance ? (<SettingsItemRightContent style={styles.rightContentPadding}>
              <Amount Numbers-16 showFraction={false} value={{
                value: balance,
                currency: account.currencyCode,
            }}/>
            </SettingsItemRightContent>) : null}
        </SettingsItem>
      </FlatListItemCard>);
});
export const renderAccountListItem = ({ item, }) => <CreditReportAccountListRow entry={item}/>;
export const CreditReportAccountsEmpty = ({ isClosed, isFetching }) => {
    const styles = useStyles(styleSet);
    if (isFetching) {
        return (<View style={styles.loader}>
        <ActivityIndicator />
      </View>);
    }
    return (<ListEmptyView happy onCard title={`No ${isClosed ? 'closed ' : ''}accounts reported`} style={styles.listEmptyView}/>);
};
const styleSet = createStyleSheets((colors) => ({
    sectionHeader: {
        marginTop: rem(24),
        marginHorizontal: rem(16),
        paddingBottom: rem(16),
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
    listEmptyView: {
        marginTop: rem(24),
        marginBottom: rem(0),
    },
    loader: {
        marginTop: rem(24),
        marginBottom: rem(0),
    },
    rightContentPadding: {
        marginLeft: rem(8),
    },
}));
